/**
 * The request ACTIONS a list card offers — Cancel (two taps) and Add the return
 * time — shared by My requests and All requests (board 2732 M3, plan DD8, R-3).
 *
 * ⭐ MOVED, NOT REWRITTEN. Every line below came out of `screens/MyRequests.tsx`
 * (the constants, the DOM id helpers, the action state, the focus manager,
 * `cancelRequest`, `saveReturnTime`, `returnTimeFor`, `cancelActionsFor` and
 * `actionsFor`), so the two screens cannot drift: one two-tap confirm, one
 * per-row error that survives a refresh, one alert-failed notice, one focus
 * rule, one set of DOM ids. My requests' own suites, UNCHANGED, are the
 * acceptance test of the move. The only edits are the three seams a screen
 * hands in: `api`, the screen's own `reload` (each list re-reads its own route
 * after an action) and `onNetworkError` (each screen shows its own error).
 * ⛔ THE SERVER IS STILL THE AUTHORITY: both routes scope the write by the same
 * predicate the lists read (`scopeClause`), so a row a screen can show is a row
 * the session may act on, and a stale row earns the 409 the handlers report.
 * ⚠️ The DOM ids are per request, and the two screens are never mounted at once
 * (`app.tsx` renders one tab), so they cannot collide.
 * ⛔ NO MONEY WORD on these controls: they sit on nurse-facing cards
 * (`home-requests.spec.tsx` carries this module's source-text money ban).
 *
 * ⭐⭐ Board 2788 M5 (plan R-2 to R-5, DD-8, DD-10, DD-11, DD-14, DD-19) - THREE
 * MORE LIMBS AND TWO LINES, on the same rules as the two above (one per-row
 * error that survives a poll, one focus manager, DOM ids spelled once):
 *   · a New or Priced dated card CHANGES its date and time in place
 *     (`POST /change`); a Confirmed dated card with no open ask ASKS for one
 *     (`POST /ask-change`); a Confirmed card ASKS TO CANCEL with two taps
 *     (`POST /ask-cancel`, "Ask to stop this repeating trip" for a repeating
 *     one, DD-10), and has no direct Cancel any more (R-4);
 *   · the highlighted line says where a change or an ask stands, in words, with
 *     a neutral chip, and a closed `History` lists what was asked and changed;
 *   · INSIDE 24 HOURS OF THE PICKUP (DD-19) a dated live card offers no change,
 *     no ask and no Cancel, and says to ring, with the number as a `tel:` link.
 * ⛔ THE SERVER IS STILL THE AUTHORITY for all of it: every limb is hidden by
 * the same predicate the route refuses on, and a form opened before the window
 * closed and sent after it gets the route's own 409 sentence, row-keyed.
 */
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ApiClient } from './api.js';
import {
  CHANGE_CUTOFF_HOURS,
  ECMB_PHONE,
  insideChangeCutoff,
  type AskKind,
  type AskState,
  type DeclineReason,
  type RequestStatus,
  type ReturnAtState,
  type SchedulePattern,
  type TimeMeaning,
} from '../shared/request-schema.js';
import { dublinWallClock } from '../shared/dublin-time.js';
import { derivePickup } from './request-form-logic.js';
import { sameDublinDay } from './format.js';
import { myRequestsCopy, requestChangeCopy, requestFormCopy } from './copy.js';
import { Button, Chip, FormField, buttonClass, formatWhenParts } from '@ecmb/ui';

/** Statuses a nurse may still cancel directly (mirrors the nurse door's
 *  `allowedFrom`; the server is the authority - a stale row just gets a 409
 *  handled below).
 *  ⛔ Board 2788 R-4 (Alex, 2026-09-03): `confirmed` LEFT this set. A Confirmed
 *  request is never cancelled from the portal; the card asks instead
 *  (`askCancelFor`), and the route answers a direct cancel 409. */
const CANCELLABLE = new Set<string>(['new', 'priced']);

/** ⭐ Board 2788 M5 (R-2): the statuses whose date and time the home changes
 *  itself, on a dated row (`changeRequestJourney`'s own conjuncts). */
const CHANGEABLE = new Set<string>(['new', 'priced']);

/** ⭐ Board 2788 M5 (DD-19): the statuses the 24-hour cut-off reaches on the card.
 *  An ended request offers nothing to cut off. */
const LIVE = new Set<string>(['new', 'priced', 'confirmed']);

/** ⭐⭐ T2088 M5 (R-1) — STATUSES ON WHICH A RETURN TIME MAY STILL BE ADDED.
 *  It is the server's `setRequestReturnAt` predicate mirrored, and the server is
 *  the authority: a stale row simply earns the 409 the handler below reports. It
 *  is deliberately NARROWER than `CANCELLABLE` — `confirmed` is absent, because a
 *  confirmed request has crossed to the kiosk and this service cannot reach the
 *  booking made there. That row gets `setReturnTimeCrossedNote` instead, so a
 *  nurse is never offered a control the route would refuse. */
const RETURN_TIME_SETTABLE = new Set<string>(['new', 'priced']);

/**
 * ⭐ THE FOUR DOM IDS THE FOCUS MANAGER AIMS AT, spelled ONCE each (ux-review
 * WARN, M5 rework). `MyResidents`' `editButtonId` records the reason: one
 * helper read by the render AND by the effect, because two spellings of an id
 * is how a focus move silently focuses nothing and nobody notices.
 * ⚠️ `returnTimeInputId` is ALSO the `<label for>` target, so it was already
 * load-bearing before this rework — it is now named rather than composed twice.
 */
const returnTimeInputId = (id: number): string => `return-time-${String(id)}`;
const returnArmButtonId = (id: number): string => `return-arm-${String(id)}`;
const cancelArmButtonId = (id: number): string => `cancel-arm-${String(id)}`;
const cancelConfirmBlockId = (id: number): string => `cancel-confirm-${String(id)}`;
// ⭐ Board 2788 M5 - the new limbs' targets, by the same once-each rule.
const changeArmButtonId = (id: number): string => `change-arm-${String(id)}`;
const changeDayId = (id: number): string => `change-day-${String(id)}`;
const changeTimeId = (id: number): string => `change-time-${String(id)}`;
const askCancelArmButtonId = (id: number): string => `ask-cancel-arm-${String(id)}`;
const askCancelBlockId = (id: number): string => `ask-cancel-${String(id)}`;
const changeLineId = (id: number): string => `change-line-${String(id)}`;

/**
 * ⭐⭐ Board 2788 M6 (step 0b, the M5 ux review) - THE NUMBER AS A PHONE CONTROL, one
 * element for both places it is offered (the 24-hour ring line and the ask to
 * cancel's "If it is soon" note), so the two cannot drift.
 *   · a `tel:` link, because the phone never closes and a tap must ring;
 *   · its NAME says who it rings ("Ring East Coast Minibus on <the number>"), with
 *     the number it shows inside that name (WCAG 2.5.3, label in name);
 *   · `.request-ring-phone` keeps it on one line and makes it at least 44px tall,
 *     in the brand's navy rather than the browser's link blue (app.css).
 * ⛔ The number is `ECMB_PHONE`'s, the one source the parity spec pins to the site.
 * ⭐⭐ Board 2788 M7 (step 0, the M6 ux review) - TWO WEIGHTS, ONE ELEMENT, by the
 * rule DESIGN.md §5.1 already gives for where a control sits:
 *   · `call`: the 24-hour ring line, where ringing IS the card's one action. A
 *     button-weight control (the catalog's `secondary`, navy) on its own line, the
 *     number on one line inside it (`.request-ring-call`);
 *   · `inline`: the ask to cancel's "If it is soon" note, where the number sits
 *     INSIDE a sentence beside the confirm's own two buttons, so it keeps the link
 *     weight (`.request-ring-phone`) rather than adding a third button there.
 * ⛔ The name, the href and the number are the same in both, so they cannot drift.
 * ⚠️ An `<a>`, never the library `Button` (which renders a `<button>`): a tap must
 * dial, so the catalog's class string is composed onto the link (`buttonClass`).
 */
function phoneControl(weight: 'call' | 'inline'): JSX.Element {
  return (
    <a
      href={ECMB_PHONE.href}
      class={weight === 'call' ? `${buttonClass('secondary')} request-ring-call` : 'request-ring-phone'}
      aria-label={requestChangeCopy.ringAria(ECMB_PHONE.display)}
    >
      {ECMB_PHONE.display}
    </a>
  );
}

/** One history line as both home-facing wires carry it (`RequestHistoryLine`,
 *  `core/nurse-request-projection.ts`). `kind` is read as a string so a kind
 *  this client does not know renders nothing rather than a guess. */
export type RequestHistoryView = {
  kind: string;
  fromAt: string | null;
  toAt: string | null;
  at: string;
  by: string;
};

/** The fields of a list row the actions read: both screens' row types carry
 *  them, so either list hands its own rows in unchanged.
 *  ⭐ Board 2788 M5: the change view (`RequestChangeView`, on both wires since
 *  M3) is OPTIONAL here, as `seen` is on the screens: an older payload carries
 *  no key, and an absent key reads as "nothing asked, nothing changed". */
export type ActionableRequest = {
  requestId: number;
  status: RequestStatus;
  schedulePattern: SchedulePattern | null;
  returnTrip: boolean;
  returnAtState: ReturnAtState;
  journeyDate: string | null;
  pickupAt?: string | null;
  timeMeaning?: TimeMeaning | null;
  homeChanged?: boolean;
  askKind?: AskKind | null;
  askState?: AskState;
  askJourneyDate?: string | null;
  askPickupAt?: string | null;
  askDeclineReason?: DeclineReason | null;
  history?: readonly RequestHistoryView[];
};

/** A refusal body as the nurse doors answer it: 409 `{ error, message }`, 400
 *  `{ errors: { field: sentence } }`. */
type RefusalBody = { message?: unknown; errors?: Record<string, unknown>; alertFailed?: boolean } | null;

/** The server's own sentence when it sends one (the 409 `message`, else the
 *  first 400 field error), so the cut-off sentence with the number and DD-12's
 *  return clash reach the home in the route's words; the generic line otherwise. */
function refusalMessage(body: RefusalBody): string {
  if (body !== null && typeof body === 'object') {
    if (typeof body.message === 'string' && body.message !== '') return body.message;
    const first = body.errors ? Object.values(body.errors).find((v) => typeof v === 'string' && v !== '') : undefined;
    if (typeof first === 'string') return first;
  }
  return requestChangeCopy.failed;
}

type MeaningKey = 'appointment' | 'collection' | 'other';
const meaningKey = (m: TimeMeaning | null | undefined): MeaningKey =>
  m === 'appointment' || m === 'collection' ? m : 'other';

/**
 * "08:15 to 09:45", or with the day on both ends when the two instants fall on
 * different Irish days ("Tue 6 Oct 08:15 to Wed 7 Oct 09:45"), so a change of
 * day is never read as a change of time. Pinned Dublin pair (`formatWhenParts`).
 * Null when either end cannot be read.
 */
function spanOf(from: string | null | undefined, to: string | null | undefined): string | null {
  if (from === null || from === undefined || to === null || to === undefined) return null;
  const a = formatWhenParts({ journeyDate: from });
  const b = formatWhenParts({ journeyDate: to });
  if (a.clock === null || b.clock === null) return null;
  return sameDublinDay(from, to)
    ? requestChangeCopy.span(a.clock, b.clock)
    : requestChangeCopy.span(`${a.date} ${a.clock}`, `${b.date} ${b.clock}`);
}

/** The day a history act happened on, in the card's Dublin day words. */
const dayOf = (iso: string): string => formatWhenParts({ journeyDate: iso }).date;

/**
 * ⭐ Board 2788 M5 (R-2 to R-5, DD-11, DD-14) - WHERE THE HOME'S CHANGE OR ASK
 * STANDS, as a chip lead and the rest of the sentence, or null. Exhaustive and
 * ordered; only a LIVE request carries a line (an ended one says it in its pill):
 *   · `homeChanged` on New or Priced (DD-14) - "Changed, waiting for…";
 *   · an open change ask - "Change asked: <what> <from> to <to>[ (collection …)],
 *     waiting for…", the times labelled by `time_meaning` (DD-11);
 *   · an open cancel ask - "Cancel asked" ("Stop asked" for a repeating trip);
 *   · an accepted change ask - "Changed: <what> now <clock>";
 *   · a declined ask - "Not changed" / "Not cancelled" / "Not stopped" and
 *     Helen's closed-list reason (DD-5), never free text (S8).
 * ⛔ No status word: the pill and the step line own `Sent`, `Seen`, `Confirmed`.
 */
export function changeLineFor(item: ActionableRequest): { lead: string; rest: string } | null {
  if (!LIVE.has(item.status)) return null;
  const copy = requestChangeCopy;
  const key = meaningKey(item.timeMeaning);
  const what = copy.timeWords[key];
  const repeating = item.journeyDate === null;
  const askState = item.askState ?? 'none';
  if (item.homeChanged === true && CHANGEABLE.has(item.status)) {
    return { lead: copy.lead.changed, rest: copy.waiting };
  }
  if (askState === 'open' && item.askKind === 'change') {
    const span = spanOf(item.journeyDate, item.askJourneyDate);
    if (span === null) return { lead: copy.lead.changeAsked, rest: copy.waiting };
    const pickupSpan = key === 'appointment' ? spanOf(item.pickupAt, item.askPickupAt) : null;
    const asked = `${what} ${span}${pickupSpan !== null ? ` ${copy.collectionBracket(pickupSpan)}` : ''}`;
    return { lead: copy.lead.changeAsked, rest: copy.askedRest(asked) };
  }
  if (askState === 'open' && item.askKind === 'cancel') {
    return { lead: repeating ? copy.lead.stopAsked : copy.lead.cancelAsked, rest: copy.waiting };
  }
  if (askState === 'accepted' && item.askKind === 'change') {
    const clock = formatWhenParts({ journeyDate: item.journeyDate }).clock;
    if (clock === null) return null;
    const pickupClock = key === 'appointment' && item.pickupAt ? formatWhenParts({ journeyDate: item.pickupAt }).clock : null;
    const now = `${clock}${pickupClock !== null ? ` ${copy.collectionBracket(pickupClock)}` : ''}`;
    return { lead: copy.lead.changed, rest: copy.nowRest(what, now) };
  }
  if (askState === 'declined') {
    const reason = (item.askDeclineReason ? copy.declineReasons[item.askDeclineReason] : undefined) ?? copy.declineFallback;
    const lead = item.askKind === 'cancel'
      ? (repeating ? copy.lead.notStopped : copy.lead.notCancelled)
      : copy.lead.notChanged;
    return { lead, rest: reason };
  }
  return null;
}

/**
 * ⭐ Board 2788 M5 (R-6 home side, DD-11) - ONE HISTORY LINE IN WORDS, or null for
 * a kind this client does not know. Times are the request's OWN stored instants
 * (`fromAt`/`toAt` are `journey_date`), labelled by `time_meaning`, and never a
 * re-derived pickup: re-deriving from today's offset would be wrong for older
 * entries (DD-11). `by` is already worded by the server (S19).
 */
export function historyLineFor(item: ActionableRequest, h: RequestHistoryView): string | null {
  const copy = requestChangeCopy;
  const what = copy.historyTimeWords[meaningKey(item.timeMeaning)];
  const at = dayOf(h.at);
  switch (h.kind) {
    case 'time-changed': {
      const span = spanOf(h.fromAt, h.toAt);
      return span === null ? null : copy.historyChanged(what, span, h.by, at);
    }
    case 'change-asked': {
      const span = spanOf(h.fromAt, h.toAt);
      return span === null ? null : copy.historyAsked(what, span, h.by, at);
    }
    case 'cancel-asked':
      return item.journeyDate === null ? copy.historyStopAsked(h.by, at) : copy.historyCancelAsked(h.by, at);
    case 'ask-declined':
      return copy.historyNotAgreed(h.by, at);
    case 'cancelled':
      return copy.historyCancelled(h.by, at);
    case 'declined':
      return copy.historyTurnedDown(h.by, at);
    default:
      return null;
  }
}

export type RequestActionsOptions = {
  api: ApiClient;
  /** The screen's own list read, run after a cancel or a saved time. */
  reload: () => Promise<void>;
  /** The screen's own network-error state, set when an action cannot reach us. */
  onNetworkError: () => void;
};

/**
 * The hook. Returns `actionsFor(item)`, the card's one `actions` slot (or
 * `undefined` when the row offers nothing), and keeps every piece of action
 * state inside, keyed by request id exactly as My requests kept it.
 */
export function useRequestActions({ api, reload, onNetworkError }: RequestActionsOptions): {
  actionsFor: (item: ActionableRequest) => JSX.Element | undefined;
} {
  // ⛔ THE LATEST SEAMS, NEVER THE TAP'S (output gate, M3 round 1). A cancel or a
  // save awaits a POST; a screen whose list read closes over its FILTERS (All
  // requests' group and staff) can change them meanwhile, and calling the
  // tap-time `reload` would re-read with the old filters and, carrying the newest
  // load number, overwrite the newer list. The refs always hold this render's.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const onNetworkErrorRef = useRef(onNetworkError);
  onNetworkErrorRef.current = onNetworkError;
  // Two-tap cancel: first tap arms this row, second tap actually cancels.
  const [armedId, setArmedId] = useState<number | null>(null);
  /**
   * ⛔⛔ T2088 M6 rework — THE ONE CASE WHERE `cancelScopeNote`'s PROMISE IS
   * FALSE, AND THE SCREEN COULD NOT RETRACT IT. That sentence now opens "The
   * office is told straight away", and the route answers `alertFailed: true`
   * exactly when it was NOT: nobody at East Coast Minibus has the message, and
   * the nurse had just been told they do. This is the same string the repo's own
   * comment calls the SHARPEST INSTANCE OF THE LIE, because it is customer-facing
   * and the cost of believing it is a bus that runs and a home that is billed.
   *
   * ⛔ THE SHAPE IS `RequestForm.tsx`'s, NOT A NEW ONE (T1101 built it there for
   * this exact case): the route's optional `alertFailed` is read off the 200,
   * kept in screen state, and rendered as a `notice`/`role="alert"` paragraph.
   * ⛔ AND IT IS KEYED TO ITS ROW for `returnError`'s measured reason: a cancel
   * CHANGES the row's status, the poll tick that follows replaces `items`, and a
   * message living inside a limb the new status no longer renders would vanish in
   * the same instant it was earned. The message is about HER TAP, not about the
   * row's state, so the row's state may not decide whether she reads it.
   * ⚠️ POSITIVE-FAILURE AND OPTIONAL, exactly as the wire is: an ABSENT key means
   * the office WAS told. A truthy-negation here would show the retraction to every
   * client that has not learned the key.
   */
  const [cancelAlertFailedId, setCancelAlertFailedId] = useState<number | null>(null);
  // ⭐ T2088 M5 — the return-time limb's own arm/typed-value/refusal, keyed the
  // same way the cancel limb is armed. Separate from `armedId` so arming one
  // never silently disarms the other on the same card.
  const [returnArmedId, setReturnArmedId] = useState<number | null>(null);
  const [returnTime, setReturnTime] = useState('');
  /**
   * ⛔⛔ THE REFUSAL IS KEYED TO ITS ROW, AND THAT IS WHAT MAKES IT SURVIVE A
   * BACKGROUND REFRESH (webapp-testing FAIL, M5 rework). It was a bare string
   * rendered INSIDE the armed limb, and the limb's first guard is the row's
   * `returnAtState`. Reproduced live: a second write lands, the tap earns its
   * honest 409, and the very next poll tick (`refresh()`, which replaces `items`
   * unconditionally) flips the row out of `'to-confirm'` — the limb, the
   * `role="alert"` and the sentence all vanish together, so the nurse's tap was
   * refused and she was told NOTHING. ⛔ THE POLL IS NOT THE DEFECT and is not
   * touched: the list must keep telling the truth. The error is simply no longer
   * a property of the ARMED limb; it is a property of the ROW, rendered whenever
   * one is set for it (see `returnTimeFor`).
   */
  const [returnError, setReturnError] = useState<{ requestId: number; message: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  /**
   * ⭐ Board 2788 M5 - THE CHANGE LIMB (a change on New or Priced, a change ask on
   * Confirmed: one row is never both, so one limb serves both doors), the ask to
   * cancel, and their refusal. The refusal is KEYED TO ITS ROW for `returnError`'s
   * measured reason: a 409 usually means the row moved, the poll that follows
   * replaces `items`, and a sentence living in the armed limb would go with it.
   * The window closing is the sharpest case: the limb is gone by the device clock
   * and the route's cut-off sentence, with the number, is what is left to read.
   */
  const [changeArmedId, setChangeArmedId] = useState<number | null>(null);
  const [changeDay, setChangeDay] = useState('');
  const [changeTime, setChangeTime] = useState('');
  const [changeError, setChangeError] = useState<{ requestId: number; message: string } | null>(null);
  const [askCancelArmedId, setAskCancelArmedId] = useState<number | null>(null);
  /** The office was NOT emailed about a saved change or ask (`alertFailed`),
   *  keyed to its row for `cancelAlertFailedId`'s reason. */
  const [changeAlertFailedId, setChangeAlertFailedId] = useState<number | null>(null);
  /**
   * ⭐ THE HOME'S CURRENT PICKUP OFFSET, for the change form's collection preview
   * (DD-3: the server derives with the CURRENT offset, never the one the request
   * was made with). `undefined` until the first appointment limb is armed, which
   * reads `/api/meta` once; `null` when none is published or the read failed, and
   * then the preview simply does not render. Read lazily so neither list issues a
   * request it did not issue before M5 on mount.
   * simplified: read once per mount, not on every arm; add a re-read when a home
   * is seen changing its offset while a list stays open.
   */
  const [offsetMinutes, setOffsetMinutes] = useState<number | null | undefined>(undefined);
  const offsetRequested = useRef(false);

  /**
   * ⭐⭐ ONE FOCUS MANAGER FOR BOTH LIMBS (ux-review WARN, M5 rework). Arming or
   * disarming either limb UNMOUNTS the button that was tapped — that is the
   * whole two-tap idiom — so without this `document.activeElement` falls to
   * `<body>` and a keyboard or screen-reader nurse restarts her tab walk at the
   * top of the document. ⚠️ PRE-EXISTING IN THE CANCEL LIMB; M5 doubled the
   * affected surfaces, which is why it is fixed now, and fixed ONCE rather than
   * twice: both limbs name a DOM id and this effect focuses it.
   *
   * ⛔ BY ID AND FROM AN EFFECT, which is `MyResidents`' mechanism (its
   * `focusAfter` / `editButtonId` pair) and for its reason: the node to focus is
   * a control in a list row that DOES NOT EXIST until the swap has rendered, so
   * focusing inside the handler would reach the node that is about to be
   * replaced. State, not a ref, because the effect has to re-run; cleared by the
   * effect so a later unrelated re-render (a poll tick) cannot steal focus a
   * second time.
   * ⛔ AND THE TARGETS ARE THE SAFE ONES. Arming the RETURN limb focuses the time
   * control itself — the thing she is being asked for. Arming the CANCEL limb
   * focuses the armed BLOCK (`tabIndex={-1}`, `Staff.tsx`'s armed-confirm
   * mechanism), never the destructive button inside it: a held Enter repeats its
   * keydown, and parking that repeat on "Yes, cancel" would destroy the request
   * the two-tap idiom exists to protect. Tab then reaches Yes, then Not yet.
   * Disarming either limb returns focus to the button that replaces the prompt.
   */
  const [focusAfterSwap, setFocusAfterSwap] = useState<string | null>(null);
  useEffect(() => {
    if (focusAfterSwap === null) return;
    document.getElementById(focusAfterSwap)?.focus();
    setFocusAfterSwap(null);
  }, [focusAfterSwap]);

  async function cancelRequest(id: number): Promise<void> {
    setBusyId(id);
    const res = await api.post<{ alertFailed?: boolean }>(`/api/requests/${String(id)}/cancel`, {});
    setBusyId(null);
    setArmedId(null);
    if (res.kind === 'network-error') {
      onNetworkErrorRef.current();
      return;
    }
    // ⛔ THE RETRACTION, READ OFF THE 200 THE WAY `RequestForm.tsx` READS ITS 201.
    // Cleared on every tap first, so a second cancel that DID reach the office
    // cannot leave the previous one's correction standing.
    setCancelAlertFailedId(res.status === 200 && res.body?.alertFailed === true ? id : null);
    // 200 cancelled; 409 means it already moved on another screen — either
    // way the reload shows the truth.
    await reloadRef.current();
  }

  /**
   * ⭐⭐ T2088 M5 (R-1's last clause) — SEND THE RETURN TIME SHE HAS SINCE LEARNED.
   * ⭐⭐ Board 2638 (plan D1) — THE SERVER COMPOSES IT, as it does on the create
   * path: this posts the typed `{ returnTime }` and the route composes it on the
   * journey's own IRISH day with `dublinInstant`, so the device's zone and clock
   * play no part. This reverses the old reasoning, that the browser must compose
   * because the server's zone "is not where the trip was booked from": the
   * server pins `Europe/Dublin` explicitly, never its own zone.
   * ⚠️ A row with no readable day has nothing to compose on, so the control is
   * not offered on it at all (see `returnTimeFor`) and this is belt-and-braces.
   * ⛔ THE SERVER'S OWN SENTENCE IS PREFERRED WHEN IT SENDS ONE. A 400 carries
   * `errors.returnAt` from the shared validator — "The return must be after the
   * outbound trip.", the same words the form would have said — and repeating the
   * generic failure there would hide a mistake she can actually fix. Everything
   * else (the 409, an unexpected status) gets the one honest sentence.
   */
  async function saveReturnTime(item: ActionableRequest): Promise<void> {
    if (item.journeyDate === null) return;
    if (returnTime.trim().length === 0) return;
    setReturnError(null);
    setBusyId(item.requestId);
    const res = await api.post<{ errors?: { returnAt?: string } }>(
      `/api/requests/${String(item.requestId)}/return-time`,
      { returnTime },
    );
    setBusyId(null);
    if (res.kind === 'network-error') {
      onNetworkErrorRef.current();
      return;
    }
    if (res.status !== 200) {
      setReturnError({
        requestId: item.requestId,
        message: res.body?.errors?.returnAt ?? myRequestsCopy.setReturnTimeFailed,
      });
      return;
    }
    setReturnArmedId(null);
    setReturnTime('');
    await reloadRef.current();
  }

  /**
   * ⭐⭐ T2088 M5 (plan Step 4) — THE RETURN-TIME LIMB, OR THE LINE THAT SENDS
   * HER TO THE PHONE, OR NOTHING. Three render rules and they are exhaustive:
   *   · not a to-confirm return → nothing. The state is the gate, never
   *     `returnAt === null` on its own: a one-way job, a pre-v14 row and a
   *     standing run all read null and none of them is "to be confirmed".
   *   · to-confirm AND still `new`/`priced` AND the day is readable → the
   *     control. A row whose day cannot be parsed has nothing to compose the
   *     instant against, so it falls to the note rather than to a control that
   *     could not send.
   *   · to-confirm and anything else (`confirmed`, i.e. crossed) → the ring-us
   *     line, in `cancelScopeNote`'s register. ⛔ A TERMINAL ROW
   *     (`cancelled` / `declined`) GETS NEITHER: there is no trip left to ring
   *     about, and a note telling her to chase one would be worse than silence.
   *
   * ⛔⛔ AND A REFUSAL SHE HAS ALREADY EARNED OUTLIVES ALL THREE (webapp-testing
   * FAIL, M5 rework). `failure` is computed FIRST and rendered on every branch,
   * including the two that render nothing else, because the row's state is
   * exactly what a refused tap tends to change: the poll tick that follows a 409
   * replaces `items`, the row leaves `'to-confirm'`, and a message that lived
   * inside the armed limb went with it. The message is about HER TAP, not about
   * the row's current state, so the row's state may not decide whether she reads
   * it. ⚠️ The two 400 paths never changed row state and are unaffected — this
   * only ADDS the branches on which the sentence used to disappear.
   */
  function returnTimeFor(item: ActionableRequest) {
    const failure =
      returnError !== null && returnError.requestId === item.requestId ? (
        <p class="screen-error" role="alert">
          {returnError.message}
        </p>
      ) : undefined;
    if (!item.returnTrip || item.returnAtState !== 'to-confirm') return failure;
    if (item.status === 'cancelled' || item.status === 'declined') return failure;
    if (!RETURN_TIME_SETTABLE.has(item.status) || item.journeyDate === null) {
      return (
        <>
          {failure}
          <p class="cancel-confirm-scope">{myRequestsCopy.setReturnTimeCrossedNote}</p>
        </>
      );
    }
    if (returnArmedId !== item.requestId) {
      // ⭐ `secondary`, the safe-but-real button the cancel limb's Keep uses: this
      // tap destroys nothing, and the only destructive grammar on this card
      // belongs to the cancel below it.
      return (
        <>
          {failure}
          <Button
            variant="secondary"
            size="full"
            id={returnArmButtonId(item.requestId)}
            onClick={() => {
              setReturnArmedId(item.requestId);
              setReturnTime('');
              setReturnError(null);
              // ⭐ THE CONTROL SHE IS BEING ASKED FOR, once it exists.
              setFocusAfterSwap(returnTimeInputId(item.requestId));
            }}
          >
            {myRequestsCopy.setReturnTimeButton}
          </Button>
        </>
      );
    }
    return (
      /* The cancel limb's shape exactly (prompt on its own line, two real
         buttons below), with the time control between them — in place of the
         button that armed it, never a modal. */
      <div class="cancel-confirm">
        {/* ⛔ THE PROMPT IS THE CONTROL'S `<label>`, not a paragraph beside it:
            the question a nurse reads and the name a screen reader announces are
            one sentence, and the e2e net queries the input by that name. */}
        <label class="cancel-confirm-prompt" for={returnTimeInputId(item.requestId)}>
          {myRequestsCopy.setReturnTimePrompt}
        </label>
        <input
          id={returnTimeInputId(item.requestId)}
          type="time"
          value={returnTime}
          onInput={(e) => setReturnTime((e.target as HTMLInputElement).value)}
        />
        {failure}
        <div class="cancel-confirm-actions">
          <Button
            variant="primary"
            disabled={busyId === item.requestId || returnTime.trim().length === 0}
            onClick={() => void saveReturnTime(item)}
          >
            {myRequestsCopy.setReturnTimeSave}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setReturnArmedId(null);
              setReturnError(null);
              // ⭐ BACK TO THE BUTTON THAT REPLACES THIS PROMPT, never `<body>`.
              setFocusAfterSwap(returnArmButtonId(item.requestId));
            }}
          >
            {myRequestsCopy.setReturnTimeKeep}
          </Button>
        </div>
      </div>
    );
  }

  /** The cancel affordance for one row: the quiet-destructive button, or the
   *  in-place confirm once armed. Rendered inside the card's full-width actions
   *  region — a COLUMN since the M5 rework, so this limb and the return-time one
   *  each take their own row (`packages/ui/styles/components.css`).
   *  ⭐ T2088 M5 — RENAMED, NOT CHANGED: `actionsFor` below now composes this
   *  limb with the return-time one, because the card takes ONE `actions` slot
   *  and a row can legitimately offer both. */
  function cancelActionsFor(item: ActionableRequest) {
    if (!CANCELLABLE.has(item.status)) return undefined;
    if (armedId !== item.requestId) {
      return (
        // ⛔ DESTRUCTIVE-QUIET (DESIGN.md §5.1): the error-600 word on the quiet
        // outline, never a filled red and never beside a primary (this screen
        // has none in its ready state).
        // ⭐ T1749 M7 — `Button`, not hand-written classes. `size="full"` is the
        // sitting's "centred and full-width" made a component choice rather than
        // a screen rule; the card's own `.request-card-actions` gives it the row.
        // ⚠️ NOT "centres it" since the M5 rework: that region is a flex COLUMN
        // whose children are 100% wide, so there is nothing left to centre.
        // ⚠️ THE LABEL IS UNCHANGED AND IS e2e-LOCKED (`Cancel this request`).
        <Button
          variant="destructive-quiet"
          size="full"
          id={cancelArmButtonId(item.requestId)}
          onClick={() => {
            setArmedId(item.requestId);
            // ⭐ THE BLOCK, NOT THE BUTTON INSIDE IT — see the focus manager
            // above for why the destructive control is the wrong target.
            setFocusAfterSwap(cancelConfirmBlockId(item.requestId));
          }}
        >
          {item.schedulePattern ? myRequestsCopy.cancelRecurringButton : myRequestsCopy.cancelButton}
        </Button>
      );
    }
    return (
      /* Prompt on its own full-width line, both choices as real buttons in a
         row below (round-3, P5), in place of the button that armed it. */
      /* ⛔ `tabIndex={-1}` IS NOT DECORATION — it is what makes this block a
         legal focus target for the manager above, the same mechanism
         `Staff.tsx`'s armed confirm and `MyResidents`' heading use. Remove it
         and `.focus()` silently does nothing, which is the state this rework
         found. It is -1, so the block is reachable by script and NOT a stop on
         the tab walk. */
      <div class="cancel-confirm" id={cancelConfirmBlockId(item.requestId)} tabIndex={-1}>
        <p class="cancel-confirm-prompt">{myRequestsCopy.cancelConfirmPrompt}</p>
        {/* ⭐⭐ T1011 M6 — AT THE DECISION, NOT AFTER IT. Between the prompt and
            the two buttons deliberately: the last thing read before the tap
            that matters. No role="note": the prompt is already plain text in
            the same block. */}
        <p class="cancel-confirm-scope">{myRequestsCopy.cancelScopeNote}</p>
        <div class="cancel-confirm-actions">
          {/* ⭐ impeccable W5 at M7: the tap that DESTROYS wears the destructive
              grammar (the kiosk's own §5.1 reference, `DestinationList.tsx`),
              and the safe choice is the bordered outline; before, the
              destroying limb was a navy fill and carried no signal at all. */}
          <Button
            variant="destructive-quiet"
            disabled={busyId === item.requestId}
            onClick={() => void cancelRequest(item.requestId)}
          >
            {myRequestsCopy.cancelConfirmYes}
          </Button>
          {/* ⭐ T1749 M7 (Q140, Alex 2026-09-11) — `secondary`, NOT the retired
              outline class.
              The portal had minted a fifth button level in its own sheet that was
              `Button secondary` under another name; the ruling re-points the four
              plain sites and deletes the rule. The safe limb still reads as a
              real button beside the destructive one, which is what round-3's P5
              asked for and what the outline was invented to deliver. */}
          <Button
            variant="secondary"
            onClick={() => {
              setArmedId(null);
              // ⭐ BACK TO THE BUTTON THAT REPLACES THIS PROMPT, never `<body>`.
              setFocusAfterSwap(cancelArmButtonId(item.requestId));
            }}
          >
            {myRequestsCopy.cancelConfirmKeep}
          </Button>
        </div>
      </div>
    );
  }

  /** Reads the home's current pickup offset once, for the collection preview. */
  function readOffsetOnce(): void {
    if (offsetRequested.current) return;
    offsetRequested.current = true;
    void (async () => {
      try {
        const res = await api.get<{ home?: { pickupOffsetMinutes?: number | null } }>('/api/meta');
        const raw = res.kind === 'response' && res.status === 200 ? res.body?.home?.pickupOffsetMinutes : null;
        setOffsetMinutes(typeof raw === 'number' ? raw : null);
      } catch {
        setOffsetMinutes(null);
      }
    })();
  }

  /**
   * ⭐⭐ Board 2788 M5 (R-2, R-3) - SAVE THE CHANGE, OR SEND THE ASK. The body is the
   * Irish day and the `HH:MM` she chose and NOTHING ELSE (S4): the server derives
   * every instant, the pickup with the home's current offset, so the device's zone
   * and clock play no part. A New or Priced row posts `/change`, a Confirmed one
   * `/ask-change`; the server re-checks the status, the cut-off and the cap.
   * ⛔ The route's own sentence is shown on a refusal (`refusalMessage`), keyed to
   * the row, and the limb stays open so she can correct a mistake she can fix.
   */
  async function saveChange(item: ActionableRequest): Promise<void> {
    if (changeDay === '' || changeTime === '') return;
    const door = CHANGEABLE.has(item.status) ? 'change' : 'ask-change';
    setChangeError(null);
    setChangeAlertFailedId(null);
    setBusyId(item.requestId);
    const res = await api.post<RefusalBody>(`/api/requests/${String(item.requestId)}/${door}`, {
      day: changeDay,
      time: changeTime,
    });
    setBusyId(null);
    if (res.kind === 'network-error') {
      onNetworkErrorRef.current();
      return;
    }
    if (res.status !== 200) {
      setChangeError({ requestId: item.requestId, message: refusalMessage(res.body) });
      return;
    }
    setChangeArmedId(null);
    setChangeAlertFailedId(res.body?.alertFailed === true ? item.requestId : null);
    await reloadRef.current();
    // ⭐ THE RESULT IS THE CARD'S OWN LINE ("Changed, waiting…" / "Change asked…"),
    // a `role="status"` the reload has just rendered; focus goes to it rather than
    // falling to `<body>` with the limb that was tapped.
    setFocusAfterSwap(changeLineId(item.requestId));
  }

  /** ⭐ Board 2788 M5 (R-4, DD-10) - the ask to cancel (or to stop a repeating
   *  trip), sent on the second tap. Nothing is cancelled: Helen answers it. */
  async function askToCancel(item: ActionableRequest): Promise<void> {
    setChangeError(null);
    setChangeAlertFailedId(null);
    setBusyId(item.requestId);
    const res = await api.post<RefusalBody>(`/api/requests/${String(item.requestId)}/ask-cancel`, {});
    setBusyId(null);
    setAskCancelArmedId(null);
    if (res.kind === 'network-error') {
      onNetworkErrorRef.current();
      return;
    }
    if (res.status !== 200) {
      setChangeError({ requestId: item.requestId, message: refusalMessage(res.body) });
    } else {
      setChangeAlertFailedId(res.body?.alertFailed === true ? item.requestId : null);
    }
    await reloadRef.current();
    setFocusAfterSwap(res.status === 200 ? changeLineId(item.requestId) : askCancelArmButtonId(item.requestId));
  }

  /**
   * ⭐⭐ Board 2788 M5 (R-2, R-3; plan step 1) - THE CHANGE LIMB, on the return-time
   * limb's precedent: one secondary button, or in its place the two controls, a
   * preview of the collection, Save (`secondary`: never a second gold primary,
   * and this screen's one primary belongs to the return limb) and "Not now"
   * (`quiet`). The labels follow `time_meaning` (DD-11): an appointment request
   * asks for an appointment time and previews the collection the server will
   * derive; a collection request asks for the collection time itself.
   * ⛔ Prefilled with the request's OWN Irish day and time (`dublinWallClock`),
   * never the device's reading of the instant.
   */
  function changeLimbFor(item: ActionableRequest, door: 'change' | 'ask') {
    const id = item.requestId;
    const copy = requestChangeCopy;
    if (changeArmedId !== id) {
      return (
        // ⭐ Board 2788 M6 (step 0b, the Helen test): on a Confirmed card the ask is
        // the exception, not the card's job, so it is QUIET; the New or Priced
        // card's own change keeps `secondary`.
        <Button
          variant={door === 'ask' ? 'quiet' : 'secondary'}
          size="full"
          id={changeArmButtonId(id)}
          onClick={() => {
            const wall = item.journeyDate !== null ? dublinWallClock(item.journeyDate) : null;
            setChangeArmedId(id);
            setChangeDay(wall?.day ?? '');
            setChangeTime(wall?.time ?? '');
            setChangeError(null);
            if (item.timeMeaning === 'appointment') readOffsetOnce();
            setFocusAfterSwap(changeDayId(id));
          }}
        >
          {door === 'change' ? copy.changeButton : copy.askChangeButton}
        </Button>
      );
    }
    const derived = item.timeMeaning === 'appointment' && changeDay !== '' && changeTime !== ''
      ? derivePickup(changeDay, changeTime, offsetMinutes ?? null)
      : null;
    // The request form's own preview wording and shape (`RequestForm.tsx`).
    const preview = derived === null
      ? ''
      : derived.pickupDate === changeDay
        ? derived.pickupTime
        : `${derived.pickupTime} (${formatWhenParts({ journeyDate: derived.pickupDate }).date})`;
    return (
      <div class="cancel-confirm">
        <p class="cancel-confirm-prompt">{door === 'change' ? copy.changePrompt : copy.askChangePrompt}</p>
        <FormField id={changeDayId(id)} type="date" label={copy.dayLabel} value={changeDay} onInput={setChangeDay} />
        <FormField
          id={changeTimeId(id)}
          type="time"
          label={copy.timeLabels[meaningKey(item.timeMeaning)]}
          value={changeTime}
          onInput={setChangeTime}
        />
        {preview !== '' && (
          <p class="field-hint">
            {requestFormCopy.collectionDerivedLabel} {preview}. {requestFormCopy.collectionDerivedHint}
          </p>
        )}
        <div class="cancel-confirm-actions">
          <Button
            variant="secondary"
            disabled={busyId === id || changeDay === '' || changeTime === ''}
            onClick={() => void saveChange(item)}
          >
            {door === 'change' ? copy.changeSave : copy.askChangeSave}
          </Button>
          <Button
            variant="quiet"
            onClick={() => {
              setChangeArmedId(null);
              setChangeError(null);
              setFocusAfterSwap(changeArmButtonId(id));
            }}
          >
            {copy.notNow}
          </Button>
        </div>
      </div>
    );
  }

  /**
   * ⭐⭐ Board 2788 M5 (R-4, DD-10) - THE ASK TO CANCEL, two taps like Cancel and
   * in its grammar (destructive-quiet arm and confirm, the safe choice secondary,
   * the armed block focused rather than the button inside it). A repeating trip
   * reads "Ask to stop this repeating trip". DD-8: a cancel ask may replace an
   * open CHANGE ask, so it is withheld only while a cancel ask is already open.
   */
  function askCancelFor(item: ActionableRequest) {
    const id = item.requestId;
    const copy = requestChangeCopy;
    const repeating = item.journeyDate === null;
    if (askCancelArmedId !== id) {
      return (
        <Button
          variant="destructive-quiet"
          size="full"
          id={askCancelArmButtonId(id)}
          onClick={() => {
            setAskCancelArmedId(id);
            setFocusAfterSwap(askCancelBlockId(id));
          }}
        >
          {repeating ? copy.askStopButton : copy.askCancelButton}
        </Button>
      );
    }
    return (
      <div class="cancel-confirm" id={askCancelBlockId(id)} tabIndex={-1}>
        <p class="cancel-confirm-prompt">{repeating ? copy.askStopPrompt : copy.askCancelPrompt}</p>
        {/* ⭐ Board 2788 M6 (step 0b): the "If it is soon, ring us" sentence carries
            the same phone control as the ring line, so a soon trip is one tap from
            a call rather than a number to find. */}
        <p class="cancel-confirm-scope">
          {copy.askCancelNote} {phoneControl('inline')}
        </p>
        {/* ⭐ Board 2788 M6 (step 0b, DESIGN.md §5.1): THE SAFE CHOICE FIRST, the
            destructive second, so the destructive never sits in first place. */}
        <div class="cancel-confirm-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setAskCancelArmedId(null);
              setFocusAfterSwap(askCancelArmButtonId(id));
            }}
          >
            {copy.notNow}
          </Button>
          <Button variant="destructive-quiet" disabled={busyId === id} onClick={() => void askToCancel(item)}>
            {repeating ? copy.askStopYes : copy.askCancelYes}
          </Button>
        </div>
      </div>
    );
  }

  /**
   * ⭐ T2088 M5 - THE CARD'S ONE `actions` SLOT, COMPOSED FROM INDEPENDENT LIMBS:
   * the return-time control (or the line that sends her to the phone) and the
   * cancel, and since board 2788 M5 the change line, the change or ask limb, the
   * ask to cancel, the 24-hour ring line and the history. Any may be absent; when
   * all are, the slot is `undefined` and the card renders no actions region at
   * all, exactly as before.
   * ⚠️ THE ORDINARY ACTIONS LEAD AND THE DESTRUCTIVE ONE IS LAST - DESIGN.md
   * §5.1's rule that a destructive control never sits first: the change line,
   * then the change (or ask), then the return time, then Cancel or the ask to
   * cancel, then the closed history.
   */
  function actionsFor(item: ActionableRequest) {
    const returnLimb = returnTimeFor(item);
    /**
     * ⭐⭐ Board 2788 M5 (DD-19) - THE 24-HOUR CUT-OFF, from the same instant and the
     * same constant the server uses (`COALESCE(pickupAt, journeyDate)`,
     * `insideChangeCutoff`). Inside it a dated live card offers no change, no ask
     * and NO CANCEL (the second ruling), and says to ring instead, with the
     * number as a `tel:` link: the phone never closes. A repeating trip has no
     * single pickup and is never inside it. The device clock decides only what is
     * OFFERED; the route decides what is accepted.
     */
    const dated = item.journeyDate !== null && item.schedulePattern === null;
    const inside = LIVE.has(item.status) && dated
      && insideChangeCutoff(item.pickupAt ?? item.journeyDate, new Date());
    const changeable = !inside && dated && CHANGEABLE.has(item.status);
    // ⭐ Board 2788 M6 (step 0b): and not while the ask-to-cancel confirm is open on
    // this card, so the question she is answering is the only one on it.
    const askable = !inside && dated && item.status === 'confirmed' && (item.askState ?? 'none') !== 'open'
      && askCancelArmedId !== item.requestId;
    const cancelAskable = !inside && item.status === 'confirmed'
      && !((item.askState ?? 'none') === 'open' && item.askKind === 'cancel');
    const changeLimb = changeable ? changeLimbFor(item, 'change') : askable ? changeLimbFor(item, 'ask') : undefined;
    const cancelLimb = inside ? undefined : cancelActionsFor(item);
    const askCancelLimb = cancelAskable ? askCancelFor(item) : undefined;
    // ⭐ Board 2788 M7 (step 0): the sentence, then the number as the card's one
    // control ON ITS OWN LINE; a fragment, so the control is a direct child of the
    // card's action column and takes its width rules (full on the phone, its own
    // width from 600px), like every other card control.
    const ringLimb = inside ? (
      <>
        <p class="cancel-confirm-prompt">{requestChangeCopy.ringLead(CHANGE_CUTOFF_HOURS).trimEnd()}</p>
        {phoneControl('call')}
      </>
    ) : undefined;
    const line = changeLineFor(item);
    // ⭐ A `role="status"` LINE, so a change Helen answers is announced when the
    // poll lands it; `tabIndex={-1}` makes it the focus target after a save.
    const lineLimb = line !== null ? (
      <p class="request-change-line" role="status" id={changeLineId(item.requestId)} tabIndex={-1}>
        <Chip neutral>{line.lead}</Chip> {line.rest}
      </p>
    ) : undefined;
    // ⛔ OUTLIVES EVERY LIMB, `returnTimeFor`'s `failure` rule: rendered whatever
    // the row's state has since become, because the state is what a refusal changes.
    const changeFailure = changeError !== null && changeError.requestId === item.requestId ? (
      <p class="screen-error" role="alert">
        {changeError.message}
      </p>
    ) : undefined;
    const historyLines = (item.history ?? [])
      .map((h) => historyLineFor(item, h))
      .filter((t): t is string => t !== null);
    const historyLimb = historyLines.length > 0 ? (
      <details>
        <summary class="request-history-summary">{requestChangeCopy.historySummary}</summary>
        <ul class="request-history-list">
          {historyLines.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </details>
    ) : undefined;
    /**
     * ⛔⛔ THE CORRECTION OUTLIVES BOTH LIMBS, and that is the whole reason it is
     * computed here rather than inside `cancelActionsFor`. A cancelled row is
     * TERMINAL: `cancelActionsFor` returns nothing for it and `returnTimeFor`
     * returns nothing either, so a notice rendered inside either limb would be
     * mounted and unmounted in the same breath as the reload that proves the
     * cancel landed. It leads, because it retracts a promise the nurse read a
     * moment ago and everything below it is about what to do next.
     */
    const alertFailedLimb =
      cancelAlertFailedId === item.requestId ? (
        <p class="notice" role="alert">
          {myRequestsCopy.cancelAlertFailedNotice}
        </p>
      ) : changeAlertFailedId === item.requestId ? (
        <p class="notice" role="alert">
          {requestChangeCopy.alertFailed}
        </p>
      ) : undefined;
    const limbs = [alertFailedLimb, lineLimb, changeFailure, ringLimb, changeLimb, returnLimb, cancelLimb, askCancelLimb, historyLimb];
    if (limbs.every((l) => l === undefined)) return undefined;
    return (
      <>
        {alertFailedLimb}
        {lineLimb}
        {changeFailure}
        {ringLimb}
        {changeLimb}
        {returnLimb}
        {cancelLimb}
        {askCancelLimb}
        {historyLimb}
      </>
    );
  }

  return { actionsFor };
}
